//
//      invite.js
//      Stage Vault
//
//      Created by Claude on 2026-07-06.
//      Full-screen organization-invitation screen (/invite/<token>). The
//      emailed link lands here; the router shows the sign-in prompt first
//      when the visitor has no session, and applyRoute() re-renders this
//      view after login/signup, so the same URL works for brand-new and
//      existing accounts. The invitation only resolves when the signed-in
//      account's email matches the invited address.
//

import $ from 'jquery';

import { v1BaseUrl } from './config.js';
import { navBarHtml } from '/js/navigation.js';
import { navigate } from '/js/router.js';
import { logoutUser } from '/js/accounts.js';

function screenShell() {
  const app = $('#app');
  app.empty();
  app.append(navBarHtml('Invitation'));

  const container = $('<div class="container d-flex justify-content-center align-items-center" style="min-height: calc(100vh - 120px);"></div>');
  const card = $('<div class="card bg-danger-subtle border-0 rounded-4 text-light text-center p-4" style="max-width: 480px; width: 100%;" id="inviteCard"></div>');
  container.append(card);
  app.append(container);
  return card;
}

function spinner() {
  return $('<div class="d-flex justify-content-center my-5"><div class="spinner-border text-light" role="status"><span class="visually-hidden">Loading...</span></div></div>');
}

// Message-only states (invalid link, wrong account, expired, ...).
function renderNotice(card, { icon, title, body, actions = [] }) {
  card.empty();
  card.append($(`<i class="bi ${icon} display-4 mb-3"></i>`));
  card.append($('<h4 class="mb-2"></h4>').text(title));
  card.append($('<p class="text-light-emphasis"></p>').text(body));
  const row = $('<div class="d-flex justify-content-center gap-2 mt-3"></div>');
  actions.forEach((a) => row.append(a));
  if (actions.length) card.append(row);
}

function vaultButton(label = 'Go to My Vault') {
  return $('<button class="btn btn-secondary"></button>').text(label).on('click', () => navigate('/'));
}

export function renderInvite(token) {
  const card = screenShell();
  card.append(spinner());

  $.ajax({
    url: v1BaseUrl + 'account/invite',
    type: 'GET',
    svErrorMode: 'none',
    xhrFields: { withCredentials: true },
    data: { token },
    dataType: 'json',
    success: (response) => renderInviteDetails(card, token, response.invite),
    error: (xhr) => {
      const body = xhr.responseJSON || {};
      if (body.code === 'EMAIL_MISMATCH') {
        const signOutBtn = $('<button class="btn btn-primary">Switch Account</button>').on('click', () => logoutUser());
        renderNotice(card, {
          icon: 'bi-person-x',
          title: 'Wrong account',
          body: `This invitation was sent to ${body.maskedEmail || 'a different email address'}. Switch to that account to respond.`,
          actions: [signOutBtn, vaultButton()],
        });
        return;
      }
      renderNotice(card, {
        icon: 'bi-envelope-x',
        title: 'Invitation not found',
        body: 'This invitation link is no longer valid.',
        actions: [vaultButton()],
      });
    },
  });
}

function renderInviteDetails(card, token, invite) {
  if (invite.status !== 'pending') {
    const copy = {
      accepted: 'You\'ve already accepted this invitation.',
      declined: 'You\'ve already declined this invitation.',
      revoked: 'This invitation has been revoked.',
      expired: 'This invitation has expired.',
    };
    renderNotice(card, {
      icon: 'bi-envelope-open',
      title: 'Invitation ' + invite.status,
      body: copy[invite.status] || 'This invitation is no longer open.',
      actions: [vaultButton()],
    });
    return;
  }

  card.empty();

  if (invite.org.icon) {
    card.append($('<img class="rounded-4 mx-auto mb-3" style="width: 96px; height: 96px; object-fit: cover;" alt="">').attr('src', invite.org.icon));
  } else {
    card.append($('<i class="bi bi-building display-3 mb-3"></i>'));
  }

  card.append($('<h4 class="mb-1">You\'ve been invited</h4>'));
  const intro = $('<p class="text-light-emphasis mb-1"></p>');
  intro.append(
    document.createTextNode((invite.invitedBy || 'An administrator') + ' invited you to help manage ')
  );
  intro.append($('<strong class="text-light"></strong>').text(invite.org.name));
  card.append(intro);
  card.append($('<p class="mb-3"></p>').append('Role: ', $('<span class="badge bg-secondary"></span>').text(invite.role)));
  card.append($('<p class="text-light-emphasis small"></p>').text('Expires ' + new Date(invite.expires.replace(' ', 'T')).toLocaleDateString()));

  const acceptBtn = $('<button class="btn btn-primary px-4">Accept</button>');
  const declineBtn = $('<button class="btn btn-outline-light px-4">Decline</button>');
  const row = $('<div class="d-flex justify-content-center gap-2 mt-2"></div>').append(acceptBtn, declineBtn);
  card.append(row);

  const respond = (action) => {
    acceptBtn.prop('disabled', true);
    declineBtn.prop('disabled', true);
    $.ajax({
      url: v1BaseUrl + 'account/inviteRespond',
      type: 'POST',
      svErrorMode: 'none',
      xhrFields: { withCredentials: true },
      data: { token, action },
      dataType: 'json',
      success: (response) => {
        if (response.accepted) {
          renderNotice(card, {
            icon: 'bi-check-circle',
            title: 'Welcome aboard!',
            body: `You now have access to ${invite.org.name}.`,
            actions: [],
          });
          // localStorage doesn't cross subdomains, so hand the CMS the org
          // to open via a boot param.
          setTimeout(() => {
            window.location.href = `https://${import.meta.env.CMS_HOSTNAME}?org=${encodeURIComponent(response.org)}`;
          }, 1500);
        } else {
          renderNotice(card, {
            icon: 'bi-envelope-slash',
            title: 'Invitation declined',
            body: 'This invitation has been declined.',
            actions: [vaultButton()],
          });
        }
      },
      error: (xhr) => {
        acceptBtn.prop('disabled', false);
        declineBtn.prop('disabled', false);
        renderNotice(card, {
          icon: 'bi-exclamation-triangle',
          title: 'Something went wrong',
          body: xhr.responseJSON?.error || 'Could not submit your response. Please try again.',
          actions: [vaultButton()],
        });
      },
    });
  };

  acceptBtn.on('click', () => respond('accept'));
  declineBtn.on('click', () => respond('decline'));
}
